<template>
	<component :is="dialogTag" v-model="isOpen" :fullscreen="props.fullscreen" :transition="dialogTransition" :persistent="persistent" :class="['dialog-control__overlay', { 'dialog-control--side': isSide }]">
		<template v-if="$slots.activator" #activator="slotProps">
			<slot name="activator" v-bind="slotProps" />
		</template>
		<v-card v-if="hasFrame" rounded="xl" class="nav-dialog dialog-control">
			<v-card-title class="nav-dialog__title dialog-control__title">
				<v-icon v-if="props.icon" color="primary">{{ props.icon }}</v-icon>
				<div class="dialog-control__heading">
					<div v-if="props.subtitle" class="dialog-control__subtitle">{{ props.subtitle }}</div>
					<div class="dialog-control__label">{{ props.title }}</div>
				</div>
				<slot name="title-append" />
				<v-btn v-if="props.closable" icon variant="text" size="small" class="dialog-control__close" @click="isOpen = false">
					<v-icon>close</v-icon>
				</v-btn>
			</v-card-title>
			<v-divider v-if="props.divided" />
			<v-card-text :class="['dialog-control__body', props.bodyClass]">
				<slot />
			</v-card-text>
			<v-card-actions v-if="$slots.actions" class="dialog-control__actions">
				<slot name="actions-prepend" />
				<v-spacer />
				<slot name="actions" />
			</v-card-actions>
		</v-card>
		<slot v-else />
	</component>
</template>

<script setup>
import { computed, inject, ref, onMounted, onUnmounted } from 'vue';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { useOverlayScrimClose } from '@/composables/useOverlayScrimClose';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	fullscreen: { type: Boolean, default: false },
	mobile: { type: Boolean, default: null },
	transition: { type: [String, Boolean, Object], default: undefined },
	title: { type: String, default: null },
	subtitle: { type: String, default: null },
	icon: { type: String, default: null },
	closable: { type: Boolean, default: true },
	divided: { type: Boolean, default: false },
	side: { type: Boolean, default: false },
	persistent: {type: Boolean, default: false},
	bodyClass: { type: [String, Array, Object], default: null },
});
const emit = defineEmits(['update:modelValue']);

const overlayTarget = inject('overlayTarget', null);

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

const isTablet = ref(deviceUtils.isTablet());
function onResize() {
	isTablet.value = deviceUtils.isTablet();
}
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const isMobile = computed(() => (props.mobile !== null ? props.mobile : isTablet.value));
const dialogTag = computed(() => (isMobile.value && !props.fullscreen ? 'v-bottom-sheet' : 'v-dialog'));
const isSide = computed(() => props.side && dialogTag.value === 'v-dialog');
const hasFrame = computed(() => !!props.title);
const dialogTransition = computed(() => {
	if (dialogTag.value === 'v-bottom-sheet') return undefined;
	if (isSide.value) return props.transition ?? 'slide-x-reverse-transition';
	return props.transition;
});

useOverlayScrimClose(
	isOpen,
	() => {
		if (props.persistent) return;
		isOpen.value = false;
	},
	overlayTarget,
	'.dialog-control__overlay'
);
</script>
